import axios, { AxiosInstance } from "axios";

export interface AxiosConfig {
  baseURL: string;
  timeoutMs?: number;
  userAgent?: string;
}

export function createAxios(config: AxiosConfig): AxiosInstance {
  const instance = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeoutMs ?? 10_000,
    headers: {
      "User-Agent": config.userAgent ?? "gamma-client/1.0.0",
      "Content-Type": "application/json",
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      console.debug(`[HTTP] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
      });
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for retry logic
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (!config || config.__retryCount >= 2) {
        return Promise.reject(error);
      }

      config.__retryCount = config.__retryCount || 0;
      config.__retryCount += 1;

      // Only retry on 429 or 5xx errors
      if (error.response?.status === 429 || error.response?.status >= 500) {
        const delay = calculateRetryDelay(config.__retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return instance(config);
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

function calculateRetryDelay(retryCount: number): number {
  const baseDelay = 400;
  const exponentialDelay = baseDelay * Math.pow(2, retryCount - 1);
  const jitter = Math.random() * 0.1 * exponentialDelay;
  return exponentialDelay + jitter;
}
